/*
 * Fereastra in care se VEDE un fisier de pe tichet.
 *
 * ---------------------------------------------------------------------------
 * DE CE O FEREASTRA, SI NU O FILA NOUA
 * ---------------------------------------------------------------------------
 *
 * Fisierele de aici sunt aproape totdeauna ceva la care omul se UITA in timp ce
 * citeste tichetul: o poza cu eroarea, un formular pe care i l-a trimis service
 * desk-ul. O fila noua l-ar fi scos din pagina si l-ar fi pus sa se intoarca.
 *
 * ---------------------------------------------------------------------------
 * DE CE UN `blob:`, SI NU ADRESA DE PE API
 * ---------------------------------------------------------------------------
 *
 * Autentificarea e prin antet, nu prin cookie - vezi `ticketFileBlobUrl`. Un
 * `src` catre api ar fi plecat fara token si ar fi aratat o poza rupta.
 *
 * Adresa creata se ELIBEREAZA la inchidere. Fara asta, fila tine in memorie
 * fiecare fisier deschis pana cand omul o inchide - si pe un tichet cu cinci
 * poze de cate patru megaocteti se simte.
 *
 * ---------------------------------------------------------------------------
 * CE E ALTFEL PE TELEFON
 * ---------------------------------------------------------------------------
 *
 * Doua lucruri, si amandoua din acelasi motiv: WebView-ul Android nu e un
 * browser intreg.
 *
 * 1. NU ARE VIZUALIZATOR DE PDF. Un `<object type="application/pdf">` cade
 *    mereu pe continutul de rezerva, deci pe nativ nici nu-l mai incercam
 *    pentru PDF-uri - vezi `showsHere`. Pozele se vad la fel ca pe web.
 *
 * 2. ADRESA `blob:` NU IESE DIN PAGINA. Traieste inauntrul originii
 *    `https://localhost` si n-o vede nici browserul in-app, nici alta
 *    aplicatie. De aceea butonul de jos nu mai e o simpla ancora cu
 *    `target="_blank"` - aia arata ca merge si nu face nimic - ci trimite
 *    fisierul prin `openFile` din `lib/platform.ts`, care il scrie pe disc si
 *    il da sistemului.
 *
 * Pe web nu se schimba nimic: aceeasi fereastra, acelasi PDF inauntru, aceeasi
 * ancora catre fila noua.
 */

import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import * as shop from '../api/shop';
import type { TicketAttachment } from '../api/types';
import { formatBytes } from '../lib/format';
import { t } from '../lib/i18n';
import { isNative, openFile } from '../lib/platform';
import { Icon } from './Icon';

interface Props {
  ticketId: string | number;
  file: TicketAttachment;
  onClose: () => void;
}

export function FilePreview({ ticketId, file, onClose }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [openFailed, setOpenFailed] = useState(false);

  /*
   * Ce se poate ARATA chiar aici, in fereastra.
   *
   * Pe web: pozele si PDF-urile, fiindca browserul are vizualizator de PDF.
   * Pe telefon: numai pozele. WebView-ul Android n-are vizualizator de PDF,
   * deci `<object>` ar arata mereu continutul de rezerva - un mesaj despre
   * ce nu se poate, intr-o fereastra deschisa anume ca sa se poata. Acolo
   * sarim peste incercare si trimitem fisierul direct in aplicatia care stie
   * sa-l deschida.
   */
  const isPdf = file.name.toLowerCase().endsWith('.pdf');
  const showsHere = file.is_image || (isPdf && !isNative);

  /* Se tine intr-un ref, nu numai in stare: curatarea de la demontare trebuie
     sa vada ULTIMA adresa, iar o functie de curatare inchisa peste stare ar
     vedea-o pe cea de la randarea in care s-a creat. */
  const created = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;

    setUrl(null);
    setFailed(false);

    shop
      .ticketFileBlobUrl(ticketId, file.id, { signal: controller.signal })
      .then((blobUrl) => {
        if (!alive) {
          /* Fereastra s-a inchis cat timp se aducea fisierul. Adresa exista
             deja, deci se elibereaza aici - altfel n-o mai vede nimeni. */
          URL.revokeObjectURL(blobUrl);
          return;
        }

        created.current = blobUrl;
        setUrl(blobUrl);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });

    return () => {
      alive = false;
      controller.abort();

      if (created.current !== null) {
        URL.revokeObjectURL(created.current);
        created.current = null;
      }
    };
  }, [ticketId, file.id]);

  /* Escape inchide. E prima tasta pe care o apasa cineva peste care s-a deschis
     ceva, si singura pe care o stie fara sa i se spuna. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /*
   * Deschiderea in afara ferestrei.
   *
   * Pe web nu se face nimic aici: ancora isi vede de treaba ei, cu `href`-ul
   * real, deci merg si clicul cu rotita, si „copiaza adresa", si „salveaza ca".
   * Pe nativ clicul se opreste si fisierul pleaca prin `openFile`, care il
   * scrie pe disc si il da sistemului - vezi comentariul de acolo.
   *
   * Blob-ul se ia inapoi din `blob:` cu un `fetch`. Pare un ocol, dar nu e:
   * adresa arata catre octetii deja adusi, care stau in memoria filei, deci
   * cererea nu atinge reteaua si nu descarca a doua oara.
   */
  async function handleOpen(event: MouseEvent<HTMLAnchorElement>): Promise<void> {
    if (!isNative || url === null) return;

    event.preventDefault();
    setOpenFailed(false);

    try {
      const response = await fetch(url);
      await openFile(await response.blob(), file.name);
    } catch {
      /*
       * Cel mai des: telefonul n-are nicio aplicatie care sa deschida tipul
       * asta de fisier. Nu e vina omului si nu are ce sa repare, deci se spune
       * simplu ca nu s-a putut - fereastra ramane deschisa, cu poza sau cu
       * numele fisierului tot acolo.
       */
      setOpenFailed(true);
    }
  }

  return (
    <div
      className="preview"
      role="dialog"
      aria-modal="true"
      aria-label={file.name}
      /* Apasarea pe fundal inchide, dar NU si cea din interior: fara
         verificarea asta, un clic pe poza ar fi inchis fereastra. */
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="preview__box">
        <header className="preview__head">
          <span className="preview__name" title={file.name}>{file.name}</span>
          <span className="table__muted">{formatBytes(file.size)}</span>

          <button type="button" className="preview__close" onClick={onClose}>
            <Icon name="close" />
            <span className="sr-only">{t('Close')}</span>
          </button>
        </header>

        <div className="preview__body">
          {failed ? (
            /* Se spune ce s-a intamplat si ce se poate face, nu doar ca a picat.
               Fisierul e tot acolo - numai aducerea lui a esuat. */
            <p className="preview__note">{t('The file could not be loaded. Reload the page and try again.')}</p>
          ) : url === null ? (
            <p className="preview__note">{t('Loading…')}</p>
          ) : file.is_image ? (
            <img className="preview__image" src={url} alt={file.name} />
          ) : showsHere ? (
            /*
             * `<object>`, nu `<iframe>`: are continut de rezerva chiar in el,
             * pentru browserele care nu deschid PDF-uri singure.
             */
            <object className="preview__pdf" data={url} type="application/pdf" aria-label={file.name}>
              <p className="preview__note">{t('This browser cannot show the file here.')}</p>
            </object>
          ) : (
            /* Nu se poate arata aici - dar se poate deschide, cu butonul de jos.
               Mesajul spune ce urmeaza, nu doar ce nu merge. */
            <p className="preview__note">{t('This file opens in another app.')}</p>
          )}
        </div>

        {url !== null ? (
          <footer className="preview__foot">
            {openFailed ? (
              <p className="preview__note">{t('No app on this device can open this file.')}</p>
            ) : null}

            {/*
              * Ramane ancora si pe nativ, chiar daca acolo clicul e oprit:
              * fara `href` elementul nu mai e o legatura pentru cititoarele de
              * ecran si nu mai poate primi focus cu tastatura. Aceeasi regula
              * ca in `ExternalLink`.
              *
              * `rel="noopener"` fiindca `blob:` deschis cu `target` primeste
              * altfel o legatura inapoi catre fereastra asta.
              */}
            <a
              className="btn btn--sm"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleOpen}
            >
              {isNative ? t('Open the file') : t('Open in a new tab')}
            </a>
          </footer>
        ) : null}
      </div>
    </div>
  );
}
